import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';

import { BubbleSVG } from '../../components/BubbleSVG';
import { Button } from '../../components/Button';
import { Container } from '../../components/Container';
import { OfferPopup } from '../../components/OfferPopup';
import { useStoreData, useStoryblokStrings } from '../../hooks';
import { CoreContext } from '../../providers';
import { media } from '../../services/media';
import { parseHTML } from '../../services/parseHTML';
import { replaceShortcodes } from '../../services/replaceShortcodes';
import { Storyblok, StoryblokSection } from '../../storyblok';
import { BubblesWrapper } from './BubblesWrapper';
import { BubbleText } from './BubbleText';
import { Heading } from './Heading';
import { Informations } from './Informations';
import { Wrapper } from './Wrapper';

/**
 * First we get the array of advantages from UpscoreAPI
 * `(scoreData.advantages[latest_year].items)` then we match the advantage
 * with the Storyblok's advantages slug.
 *
 * Future improvements:
 * - Only name and offer are really needed from API, we can move the type to
 * Storyblok.
 */

export const ScoreAdvantages = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ScoreAdvantages>) => {
    const navigate = useNavigate();
    const [informations, setInformations] = useState('');
    const advantages = useStoreData().storyblok.AdvantageItems.items;

    const storyblokGeneralStrings = useStoryblokStrings('general-strings');

    const { scoreData } = useStoreData().user;

    const { openPopup } = useContext(CoreContext);

    const getAdvantageData = useCallback(
        (advantageName) => {
            return (
                (advantages &&
                    advantages.find((a: any) => a.slug === advantageName)) ||
                null
            );
        },
        [advantages],
    );

    if (!sectionData || !scoreData || !scoreData.bestScore) {
        return null;
    }

    const { headline, component } = sectionData;

    if (!headline) {
        return null;
    }

    // advantages
    const year = Object.keys(scoreData.bestScore).reverse()[0];
    const score = scoreData.bestScore[year];
    const advantagesData = scoreData.advantages;

    const globalTemplateVariables = { score };

    if (
        !advantagesData.length ||
        !storyblokGeneralStrings ||
        !globalTemplateVariables
    ) {
        return null;
    }

    if (informations) {
        return (
            <Section data-block={component}>
                <Container sizeGroup="size-1">
                    <Informations>
                        {parseHTML(informations, 'markdown')}
                    </Informations>
                </Container>
            </Section>
        );
    }

    return (
        <>
            {!informations && advantages && (
                <Section data-block={component}>
                    <Container sizeGroup="size-1">
                        <Wrapper>
                            <div>
                                {headline && (
                                    <Heading>
                                        {replaceShortcodes(headline, { score })}
                                    </Heading>
                                )}
                            </div>
                            <BubblesWrapper>
                                {advantagesData.map(
                                    (
                                        { name, type, link, offer }: any,
                                        index: number,
                                    ) => {
                                        const data =
                                            getAdvantageData(name)?.content;

                                        if (!data) return;

                                        const { text, informations: info } =
                                            data;

                                        return (
                                            <BubbleSVG
                                                backgroundSvgName={`bubbleMyScore${
                                                    index + 1
                                                }`}
                                                centerContentVertically
                                                key={index}
                                            >
                                                <BubbleText>
                                                    <h3>
                                                        <span>
                                                            {index + 1}.
                                                        </span>
                                                        {text}
                                                    </h3>
                                                    {storyblokGeneralStrings.click_here_button && (
                                                        <Button
                                                            variant="simple"
                                                            onClick={() => {
                                                                switch (type) {
                                                                    case 'link':
                                                                        navigate(
                                                                            link,
                                                                        );
                                                                        return;
                                                                    case 'offer':
                                                                        openPopup(
                                                                            <OfferPopup
                                                                                {...offer}
                                                                            />,
                                                                        );
                                                                        return;
                                                                    case 'informations':
                                                                    default:
                                                                        setInformations(
                                                                            info,
                                                                        );
                                                                }
                                                            }}
                                                        >
                                                            {
                                                                storyblokGeneralStrings.click_here_button
                                                            }
                                                        </Button>
                                                    )}
                                                </BubbleText>
                                            </BubbleSVG>
                                        );
                                    },
                                )}
                            </BubblesWrapper>
                        </Wrapper>
                    </Container>
                </Section>
            )}
        </>
    );
};

const Section = styled.section`
    ${media.max('md')} {
        padding: 40px 0;
    }

    ${media.min('md')} {
        padding: 80px 0;
    }
`;
